import type { Annotation, FigureAnnotation, InkTool, Point, ShapeKind, TextAnnotation } from '../../shared/protocol';
import { ANNOTATION_FONT, inkGeometry, replayPath, shapePaths, textBaselineY, type InkGeometry } from '../../shared/geometry';

const geometryCache = new WeakMap<Annotation, InkGeometry>();

export function drawInk(
  ctx: CanvasRenderingContext2D,
  tool: InkTool,
  color: string,
  size: number,
  points: readonly Point[],
  geometry?: InkGeometry,
): void {
  const g = geometry ?? inkGeometry(tool, size, points);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const run of g.runs) {
    ctx.lineWidth = run.width;
    ctx.beginPath();
    replayPath(run.path, ctx);
    ctx.stroke();
  }
  for (const d of g.dots) {
    ctx.beginPath();
    ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

export interface ShapeDraw {
  shape: ShapeKind;
  color: string;
  size: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function drawShape(ctx: CanvasRenderingContext2D, s: ShapeDraw): void {
  const p = shapePaths(s);
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.size;
  ctx.lineCap = 'round';
  ctx.lineJoin = p.join;
  ctx.beginPath();
  replayPath(p.stroke, ctx);
  ctx.stroke();
  if (p.fill) {
    ctx.beginPath();
    replayPath(p.fill, ctx);
    ctx.fill();
  }
}

export function drawText(ctx: CanvasRenderingContext2D, t: Pick<TextAnnotation, 'text' | 'x' | 'y' | 'fontSize' | 'color'>): void {
  ctx.fillStyle = t.color;
  ctx.font = `${t.fontSize}px "${ANNOTATION_FONT}", sans-serif`;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  t.text.split('\n').forEach((line, i) => {
    if (line) ctx.fillText(line.replace(/\t/g, '    '), t.x, textBaselineY(t.y, t.fontSize, i));
  });
}

export function drawFigure(ctx: CanvasRenderingContext2D, f: Pick<FigureAnnotation, 'x' | 'y' | 'texts' | 'lines' | 'color'>): void {
  ctx.strokeStyle = f.color;
  ctx.fillStyle = f.color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const [x1, y1, x2, y2, w] of f.lines) {
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(f.x + x1, f.y + y1);
    ctx.lineTo(f.x + x2, f.y + y2);
    ctx.stroke();
  }
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  for (const t of f.texts) {
    ctx.font = `${t.s}px "${ANNOTATION_FONT}", sans-serif`;
    ctx.fillText(t.t, f.x + t.x, f.y + t.y);
  }
}

export function drawAnnotation(ctx: CanvasRenderingContext2D, a: Annotation): void {
  switch (a.kind) {
    case 'ink': {
      let g = geometryCache.get(a);
      if (!g) {
        g = inkGeometry(a.tool, a.size, a.points);
        geometryCache.set(a, g);
      }
      drawInk(ctx, a.tool, a.color, a.size, a.points, g);
      return;
    }
    case 'shape':
      drawShape(ctx, a);
      return;
    case 'text':
      drawText(ctx, a);
      return;
    case 'figure':
      drawFigure(ctx, a);
      return;
  }
}
